import type { Part, Reservation, ReservationStatus, StockLevel, StockState } from '@/types'

/**
 * Availability is always derived, never stored, so it cannot drift out of step
 * with the reservations that produced it.
 *
 *   available = qty - sold - held
 *   held      = units on pending or confirmed reservations
 */

/**
 * Only these two tie stock up. Anything else — denied, released, sold — has
 * already left the hold, so freeing stock is a consequence of the status
 * changing rather than a second write that could fail on its own.
 */
export const HOLDING_STATUSES = ['pending', 'confirmed'] as const

/** A hold can only be lifted while it is still a hold. */
export const RELEASABLE_STATUSES = HOLDING_STATUSES

export function isHolding(status: ReservationStatus): boolean {
  return (HOLDING_STATUSES as readonly string[]).includes(status)
}

export function canRelease(status: ReservationStatus): boolean {
  return (RELEASABLE_STATUSES as readonly string[]).includes(status)
}

export function heldFor(partId: string, reservations: readonly Reservation[]): number {
  return reservations
    .filter((r) => r.partId === partId && isHolding(r.status))
    .reduce((n, r) => n + Math.max(0, Math.floor(r.qty || 1)), 0)
}

export function stockLevel(part: Part, reservations: readonly Reservation[]): StockLevel {
  const total = Math.max(0, Math.floor(part.qty || 0))
  const sold = Math.max(0, Math.floor(part.sold || 0))
  const held = heldFor(part.id, reservations)
  return { total, sold, held, available: Math.max(0, total - sold - held) }
}

export function stockState(part: Part, reservations: readonly Reservation[]): StockState {
  const { available, held } = stockLevel(part, reservations)
  if (available > 0) return 'available'
  if (held > 0) return 'held'
  return 'sold'
}

/**
 * Says whether it can be reserved, not whether it is here: that is the arrival
 * line's job, and "Available" or "in stock" beside "Arriving soon" contradicts it.
 */
export function stockLabel(part: Part, reservations: readonly Reservation[]): string {
  const level = stockLevel(part, reservations)
  if (level.available > 0) return level.total > 1 ? `${level.available} left` : 'Reservable'
  if (level.held > 0) return 'On hold'
  return 'Sold out'
}

/** Guards the reserve form: you cannot hold more than is free. */
export function canReserve(part: Part, reservations: readonly Reservation[], qty: number): boolean {
  const wanted = Math.floor(qty)
  if (!Number.isFinite(wanted) || wanted < 1) return false
  return wanted <= stockLevel(part, reservations).available
}

/**
 * Guards the admin edit form. Quantity may never drop below what is already
 * spoken for, or the books stop balancing.
 */
export function validateStockEdit(
  next: { qty: number; sold: number },
  held: number,
): { ok: true } | { ok: false; reason: string } {
  const qty = Math.floor(next.qty)
  const sold = Math.floor(next.sold)
  if (!Number.isFinite(qty) || qty < 1) return { ok: false, reason: 'Quantity has to be at least 1.' }
  if (!Number.isFinite(sold) || sold < 0) return { ok: false, reason: "Units sold can't be negative." }
  if (sold > qty) return { ok: false, reason: "Units sold can't be more than the quantity." }
  if (sold + held > qty) {
    return {
      ok: false,
      reason: `${held} unit${held === 1 ? ' is' : 's are'} on hold, so sold plus held can't exceed ${qty}.`,
    }
  }
  return { ok: true }
}

/** Next reference number, e.g. EC-3042. Sequence lives in a Firestore counter. */
export function reservationRef(seq: number): string {
  return `EC-${seq}`
}
